import { fromUnixTime, getUnixTime, isBefore } from 'date-fns';
import { groupBy } from 'lodash-es';
import type { QueryKey, UseQueryOptions } from '@soundxyz/graphql-react-query/reactQuery';
import { useQuery } from '@soundxyz/graphql-react-query/reactQuery';
import { useStableCallback } from '@soundxyz/graphql-react-query/utils';
import type {
  DefaultSchedule,
  MerkleSchedule,
  SuperMinterSchedule,
} from '@soundxyz/tiered-sdk-viem';
import { SUPER_MINTER_ABI } from '@soundxyz/tiered-sdk-viem/abi/super-minter';
import type { GetEditionContractInfoReturnType } from '@soundxyz/tiered-sdk-viem/actions/public/getEditionContractInfo';
import { getManyFromList, LazyPromise, type Maybe } from '@soundxyz/utils';
import { assertAddress } from '@soundxyz/utils/address';
import { getSoundContractAddressesConstants } from '@components/Dashboard/upload-new/constants';
import { callIfFunction } from '@components/modals/Buy/helpers';
import { useAuth } from '@context/auth/base';
import { GraphQLReactQuery } from '@graphql/reactQuery';
import { sdkSoundAPI } from '@hooks/useSoundSdk';
import { useTieredSdk } from '@hooks/useTieredSdk';
import { bigIntToNumber } from '@utils/bigNumberHelpers';
import { logger } from '@utils/logger';
import { isOpenEdition } from '../../shared-helpers';

/**
 * We are using this key to store the data in `react-query` cache
 */
const TIER_SDK_KEY = 'soundxyz-tiers-sdk';

/**
 * We always have it as `0`
 *
 * Context: https://soundxyz.slack.com/archives/C05BEM19ALF/p1695924405636709?thread_ts=1695924393.270319&cid=C05BEM19ALF
 */
const GA_TIER_NUMBER = 0;

type Schedule = SuperMinterSchedule;
type TierInfo = GetEditionContractInfoReturnType['tierInfo'][number];
type SaleType = 'fixed' | 'open-edition';
export type ScheduleWithTierInfo<T = Schedule> = {
  tierInfo: TierInfo;
} & T;

/**
 * enrich the `tier` property on the schedule with the tier information instead of just the tier number
 */
function mergeTierInfoWithSchedule({
  tiers,
  schedules,
}: {
  tiers: readonly TierInfo[];
  schedules: readonly Schedule[];
}): Array<ScheduleWithTierInfo> {
  const groupedTier = groupBy(tiers, ({ tier }) => tier);

  return schedules.map(schedule => {
    /**
     * We should have only 1 tier info for each tier number
     * Context: https://soundxyz.slack.com/archives/C05BEM19ALF/p1691781162656659
     */
    const tierInfo = groupedTier[schedule.tier]?.[0];

    if (!tierInfo) {
      throw new Error('Tier info not found');
    }

    return {
      ...schedule,
      tierInfo,
    };
  });
}

/**
 * Helper function to get the GA tier schedule from the mint schedules
 */
function getGaTierSchedule(mintSchedules: readonly ScheduleWithTierInfo[]) {
  return mintSchedules.filter(schedule => schedule.tierInfo.tier === GA_TIER_NUMBER);
}

/**
 * Helper function to get the VIP tier schedule from the mint schedules
 */
function getVipTierSchedules(mintSchedules: readonly ScheduleWithTierInfo[]) {
  return mintSchedules.filter(schedule => schedule.tierInfo.tier !== GA_TIER_NUMBER);
}

export const getMinSupply = (schedule: ScheduleWithTierInfo): number => {
  return schedule.mode === 'VERIFY_MERKLE'
    ? schedule.maxMintable
    : schedule.tierInfo.maxMintableLower;
};

export const getMaxSupply = (schedule: ScheduleWithTierInfo): number => {
  /**
   * If artist sets the cut off time to be in the past, then we should treat it as a fixed edition
   * Context: https://soundxyz.slack.com/archives/C05BEM19ALF/p1691782615620279?thread_ts=1691781779.907039&cid=C05BEM19ALF
   */
  if (schedule.tierInfo.cutoffTime < getUnixTime(Date.now())) {
    return schedule.tierInfo.maxMintableLower;
  }

  if (schedule.mode === 'VERIFY_MERKLE') {
    return schedule.maxMintable;
  }

  return schedule.tierInfo.maxMintableUpper;
};

/**
 * Helper function to get the sale type for a given schedule
 */
export function getSaleType(schedule: ScheduleWithTierInfo): SaleType {
  if (isOpenEdition(getMaxSupply(schedule))) {
    return 'open-edition';
  }

  if (schedule.tierInfo.maxMintableLower === schedule.tierInfo.maxMintableUpper) {
    return 'fixed';
  }

  // this is us making an open edition that was converted to fixed edition
  return 'fixed';
}

/**
 *
 * There are 2 types of tiers that are supported on Sound.
 * - GA: 0
 * - VIP: not 0
 *
 * Each of these tiers can have multiple schedules. Each schedule can have different minting modes.
 * - Public (DEFAULT)
 * - Merkle (VERIFY_MERKLE)
 * - Signature (VERIFY_SIGNATURE)
 *
 * On a tier level is where artist configure the sale type
 * - Fixed Edition
 *    -  Min !== Max
 * - Open Edition
 *    - Min: 1
 *    - Max: uint32.max
 *
 * This function will return the schedules for each of the modes for each of the tier as to what sounds's current product supports.
 *
 * - GA Tier
 *  - Public sale: Only 1 schedule
 *
 * - VIP Tier
 *   - Public sale: only 1 schedule
 *   - Merkle: only 1 schedule
 *
 */
function getSchedulesForSaleMode(mintSchedules: readonly ScheduleWithTierInfo[]): {
  gaTier: {
    saleType: Maybe<SaleType>;
    public: Maybe<ScheduleWithTierInfo<DefaultSchedule>>;
  };
  vipTier: {
    saleType: Maybe<SaleType>;
    public: Maybe<ScheduleWithTierInfo<DefaultSchedule>>;
    presale: Maybe<ScheduleWithTierInfo<MerkleSchedule>>;
  };
} {
  return {
    gaTier: (() => {
      // we grab out all the GA tier schedules
      const gaTierSchedules = getGaTierSchedule(mintSchedules);

      // our product only supports 1 public mint schedule for GA tier
      const publicMintSchedules = getManyFromList(gaTierSchedules, schedule => {
        if (schedule.mode === 'DEFAULT') {
          return schedule;
        }
        return null;
      });

      // if there are more than 1 public mint schedule, we want to get notified so we can assess the situation
      if (publicMintSchedules.length > 1) {
        logger.error({ mintSchedules }, 'GA tier can only have 1 public mint schedule');
      }

      // if there are any other types of sale modes, we want to get notified so we can assess the situation
      if (gaTierSchedules.length !== publicMintSchedules.length) {
        logger.error(
          { mintSchedules },
          "GA tier cannot have any other sale modes other than 'public'",
        );
      }

      const schedule = publicMintSchedules[0];
      return {
        saleType: schedule ? getSaleType(schedule) : null,
        public: schedule,
      };
    })(),
    vipTier: (() => {
      const vipTierSchedules = getVipTierSchedules(mintSchedules);

      const publicSaleSchedules = getManyFromList(vipTierSchedules, schedule => {
        if (schedule.mode === 'DEFAULT') {
          return schedule;
        }
        return null;
      });

      const merkleMintSchedules = getManyFromList(vipTierSchedules, schedule => {
        if (schedule.mode === 'VERIFY_MERKLE') {
          return schedule;
        }
        return null;
      });

      const signatureMintSchedules = getManyFromList(vipTierSchedules, schedule => {
        if (schedule.mode === 'VERIFY_SIGNATURE') {
          return schedule;
        }
        return null;
      });

      // We do not support signature minting, we want to get notified so we can assess the situation
      if (signatureMintSchedules.length > 0) {
        logger.error({ mintSchedules }, 'VIP tier does not support signature minting');
      }

      if (publicSaleSchedules.length > 1) {
        logger.error({ mintSchedules }, 'VIP tier can only have 1 public mint schedule');
      }

      if (merkleMintSchedules.length > 1) {
        logger.error({ mintSchedules }, 'VIP tier can only have 1 merkle mint schedule');
      }

      return {
        // All VIP tier schedule have same info so they will have same sale type.
        saleType: vipTierSchedules?.[0] ? getSaleType(vipTierSchedules[0]) : null,
        public: publicSaleSchedules?.[0],
        presale: merkleMintSchedules?.[0],
      };
    })(),
  };
}

export function useMintSchedule({
  chainId,
  editionAddress,
}: {
  chainId: number;
  editionAddress: string;
}) {
  assertAddress(editionAddress);
  const { tieredProvider } = useTieredSdk();
  const clientProvider = tieredProvider(chainId);

  return useQuery({
    queryKey: [TIER_SDK_KEY, 'mint-schedules', chainId, editionAddress],
    queryFn: async () => {
      const [info, { schedules }] = await Promise.all([
        clientProvider.getEditionContractInfo({
          edition: editionAddress,
        }),
        clientProvider.getMintingSchedules({
          editionAddress,
        }),
      ]);

      const tieredSchedules = mergeTierInfoWithSchedule({
        tiers: info.tierInfo,
        schedules: schedules,
      });

      return {
        info,
        schedules: getSchedulesForSaleMode(tieredSchedules),
      };
    },
  });
}

function getScheduleId(schedule: ScheduleWithTierInfo) {
  return `${schedule.tierInfo.tier}-${schedule.scheduleNum}`;
}

export function useTiersMintPrice({
  editionAddress,
  mintSchedule,
  chainId,
  quantity,
}: {
  editionAddress: string;
  mintSchedule: Maybe<ScheduleWithTierInfo>;
  chainId: number;
  quantity: number;
}) {
  const { tieredProvider } = useTieredSdk();

  const { data: totalMintPriceWei } = useQuery({
    queryKey: [
      TIER_SDK_KEY,
      'mint-price',
      editionAddress,
      chainId,
      !!mintSchedule && getScheduleId(mintSchedule),
      quantity,
    ],
    async queryFn() {
      if (!mintSchedule) return null;

      assertAddress(editionAddress);

      const { total, subTotal } = await tieredProvider(chainId).getTotalMintPriceAndFees({
        quantity,
        scheduleNum: mintSchedule.scheduleNum,
        tier: mintSchedule.tierInfo.tier,
        edition: editionAddress,
      });

      return { total, subTotal, fees: total - subTotal };
    },
    enabled: !!mintSchedule,
  });

  return {
    totalMintPriceWei,
  };
}

function eligibleQuantityQueryKey({
  mintSchedule,
  userAddress,
  editionAddress,
}: {
  mintSchedule: Maybe<ScheduleWithTierInfo>;
  userAddress: Maybe<string>;
  editionAddress: Maybe<string>;
}): QueryKey {
  return [
    TIER_SDK_KEY,
    'edition-eligible-quantity',
    editionAddress,
    mintSchedule?.tierInfo.tier,
    mintSchedule?.scheduleNum,
    userAddress,
  ] as const;
}

export function useTiersEditionEligibleQuantity<QueryData = bigint | null>(
  {
    mintSchedule,
    chainId,
    editionAddress,
  }: {
    mintSchedule: Maybe<ScheduleWithTierInfo>;
    chainId: number;
    editionAddress: string;
  },
  options?: Omit<
    UseQueryOptions<bigint | null, Error, QueryData, ReturnType<typeof eligibleQuantityQueryKey>>,
    'queryKey' | 'queryFn'
  >,
) {
  const { authUserAddress: userAddress } = useAuth();
  const { tieredProvider } = useTieredSdk();

  const getEligibleQuantity = useStableCallback(
    async ({
      mintSchedule,
      userAddress,
      editionAddress,
    }: {
      mintSchedule: Maybe<ScheduleWithTierInfo>;
      userAddress: Maybe<string>;
      editionAddress: Maybe<string>;
    }) => {
      if (!mintSchedule || !userAddress || !editionAddress) return 0n;

      assertAddress(userAddress);
      assertAddress(editionAddress);

      const eligibleQuantity = await tieredProvider(chainId)
        .getMintEligibility({
          collectorAddress: userAddress,
          editionAddress,
          scheduleNum: mintSchedule.scheduleNum,
          tier: mintSchedule.tierInfo.tier,
          merkleProvider: {
            merkleProof: ({ userAddress, merkleRoot }) => {
              return sdkSoundAPI.merkleProof({
                userAddress,
                merkleRoot,
              });
            },
          },
        })
        .then(v => BigInt(v.remainingEligibility));

      GraphQLReactQuery.setQueryData(
        eligibleQuantityQueryKey({ mintSchedule, userAddress, editionAddress }),
        eligibleQuantity,
      );

      return eligibleQuantity;
    },
  );

  const result = useQuery({
    queryKey: eligibleQuantityQueryKey({ mintSchedule, userAddress, editionAddress }),
    queryFn() {
      return getEligibleQuantity({
        mintSchedule,
        userAddress,
        editionAddress,
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && !!mintSchedule && !!userAddress,
  });

  return {
    ...result,
    getEligibleQuantity,
  };
}

/**
 * This hook is used to check if the user has access to the mint schedule regardless of how many editions they have minted
 */
export function useHasTieredScheduleEligibility({
  mintSchedule,
  editionAddress,
}: {
  mintSchedule: Maybe<ScheduleWithTierInfo>;
  editionAddress: string;
}) {
  const { authUserAddress } = useAuth();

  return useQuery({
    queryKey: [
      TIER_SDK_KEY,
      'tiered-has-schedule-access',
      editionAddress,
      authUserAddress,
      mintSchedule?.tierInfo.tier,
      mintSchedule?.mode,
    ] as const,
    async queryFn() {
      if (!authUserAddress || !mintSchedule) return false;

      const scheduleMerkleInfo =
        mintSchedule.mode === 'VERIFY_MERKLE' ? { merkleRoot: mintSchedule.merkleRoot } : null;

      if (!scheduleMerkleInfo) return true;

      const proof = await sdkSoundAPI.merkleProof({
        merkleRoot: scheduleMerkleInfo.merkleRoot,
        userAddress: authUserAddress,
      });

      if (!proof?.length) {
        return false;
      } else {
        return true;
      }
    },
    enabled: !!mintSchedule,
  });
}

async function hasAccessToTieredDrop({
  schedule,
  userAddress,
  mintedByUserFromSchedule,
}: {
  schedule: ScheduleWithTierInfo;
  userAddress: string;
  mintedByUserFromSchedule: Promise<bigint>;
}): Promise<boolean> {
  const { startTime, endTime, paused, maxMintable, minted, maxMintablePerAccount } = schedule;
  // check valid mint time
  if (startTime === endTime || paused) {
    return false;
  }

  // Checks for child minter custom logic
  if (schedule.mode === 'VERIFY_MERKLE') {
    const proof = await sdkSoundAPI.merkleProof({
      merkleRoot: schedule.merkleRoot,
      userAddress,
    });

    if (!proof?.length) {
      return false;
    }
  }

  if (schedule.mode === 'DEFAULT') {
    const maxQty =
      startTime < schedule.tierInfo.cutoffTime
        ? schedule.tierInfo.maxMintableUpper
        : schedule.tierInfo.maxMintableLower;

    if (minted >= maxQty) {
      return false;
    }
  }

  const editionMaxMintable = callIfFunction(schedule.tierInfo.maxMintable);
  const editionTotalMinted = schedule.tierInfo.minted;

  const editionRemainingQty = editionMaxMintable - editionTotalMinted;

  if (!editionRemainingQty) return false;

  // Get eligible quantity for the user on this mint schedule.
  const remainingForSchedule = maxMintable - minted;
  const eligibleForUserOnSchedule =
    maxMintablePerAccount - bigIntToNumber(await mintedByUserFromSchedule, { strict: false });
  const scheduleEligibleQty = Math.min(remainingForSchedule, eligibleForUserOnSchedule);

  // Return the minimum of the two. The number of tokens minted within the mint schedule
  // can never exceed the number of tokens available for the edition.
  return Math.max(0, Math.min(scheduleEligibleQty, editionRemainingQty)) !== 0;
}

export function useHasAccessToTieredSchedule({
  mintSchedule,
  editionAddress,
  chainId,
}: {
  mintSchedule: Maybe<ScheduleWithTierInfo>;
  editionAddress: string;
  chainId: number;
}) {
  const { authUserAddress } = useAuth();
  const { tieredProvider } = useTieredSdk();

  return useQuery({
    enabled: !!mintSchedule,
    queryKey: [
      TIER_SDK_KEY,
      'tiered-has-schedule-access',
      editionAddress,
      authUserAddress,
      mintSchedule?.tier,
      mintSchedule?.scheduleNum,
      mintSchedule?.mode,
      mintSchedule?.startTime,
      chainId.toString(),
    ] as const,
    queryFn(): Promise<boolean> | boolean {
      if (mintSchedule?.tierInfo.mintConcluded) return false;

      if (!authUserAddress || !mintSchedule) return false;

      const mintedByUserFromSchedule = LazyPromise(async () => {
        assertAddress(authUserAddress);

        assertAddress(editionAddress);

        return BigInt(
          await tieredProvider(chainId).readContract({
            abi: SUPER_MINTER_ABI,
            address: getSoundContractAddressesConstants(chainId).superMinterV1,
            functionName: 'numberMinted',
            args: [editionAddress, mintSchedule.tier, mintSchedule.scheduleNum, authUserAddress],
          }),
        );
      });

      return hasAccessToTieredDrop({
        schedule: mintSchedule,
        userAddress: authUserAddress,
        mintedByUserFromSchedule,
      });
    },
  });
}

export const getMintExpiry = (schedule: ScheduleWithTierInfo): number => {
  return schedule.mode === 'DEFAULT' ? schedule.tierInfo.cutoffTime : schedule.endTime;
};

export const hasSaleEnded = ({ schedule }: { schedule: ScheduleWithTierInfo }) => {
  const type = getSaleType(schedule);
  const maxSupply = getMaxSupply(schedule);
  const mintExpiryMs = getMintExpiry(schedule);

  const collected = schedule.minted;

  const isOe = type === 'open-edition';
  const isPresale = schedule.mode === 'VERIFY_MERKLE';
  const mintExpiryTime = mintExpiryMs ? fromUnixTime(mintExpiryMs) : 0;

  const now = new Date();
  const timeExpired = now > mintExpiryTime;

  const saleEnded = (() => {
    // open edition is considered ended when the mint expiry time is reached
    if (isOe) return timeExpired;
    // fixed presale is considered ended when the mint expiry time is reached
    if (isPresale) return timeExpired;
    // fixed sale is considered ended when the max supply is met
    return collected === maxSupply;
  })();

  return saleEnded;
};

export const getCollectedAmount = ({ schedule }: { schedule: ScheduleWithTierInfo }) => {
  if (schedule.mode === 'VERIFY_MERKLE') {
    return schedule.minted;
  }

  return schedule.tierInfo.minted;
};

export const isScheduleSoldOut = ({ schedule }: { schedule: ScheduleWithTierInfo }) => {
  const type = getSaleType(schedule);
  const minSupply = getMinSupply(schedule);
  const maxSupply = getMaxSupply(schedule);

  const collected = getCollectedAmount({ schedule });
  const isOe = type === 'open-edition';

  const saleEnded = hasSaleEnded({ schedule });

  if (isOe && saleEnded) return true;
  if (saleEnded) return collected >= minSupply;
  return collected >= maxSupply;
};

export const hasSaleStarted = ({ startTimestamp }: { startTimestamp: number }) => {
  const startTime = fromUnixTime(startTimestamp);
  const now = new Date();
  return isBefore(startTime, now);
};
